import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { COLORS } from '@cuemath/comps';
import { SkipBack } from 'styled-icons/feather/SkipBack';
import { Play } from 'styled-icons/feather/Play';
import { Pause } from 'styled-icons/feather/Pause';
import { SkipForward } from 'styled-icons/feather/SkipForward';
import { Plus } from 'styled-icons/feather/Plus';
import { formatTime } from '../../../utils';
import { useTrialPermissions } from '../../../hooks/useTrialDBHelper';
import { AI_ANALYSIS_SEVERITY_COLORS } from '../../../constants/trial-annotation';

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: ${COLORS.BLACK};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background 0.2s ease;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.4);
  }
`;

const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;

  svg {
    width: 40px;
    height: 40px;
    color: #1a1a1a;
    stroke-width: 2.5;
    margin-left: 4px;
  }

  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 1);
  }
`;

const ControlsContainer = styled.div`
  padding: 12px 16px;
  background: #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ProgressContainer = styled.div`
  margin-bottom: 16px;
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  background: #c4c4c4;
  border-radius: 3px;
  cursor: pointer;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #1a1a1a;
  border-radius: 3px;
  transition: width 0.1s ease;
  width: ${props => props.progress}%;
`;

const AnnotationMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  ${props =>
    props.emotion === 'positive'
      ? `background: ${COLORS.GREEN};`
      : `background: ${COLORS.ORANGE};`}
`;

const AIAnalysisMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 12;
  background: ${props => props.$color};
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #5a5a5a;
  font-family: monospace;
  font-weight: 500;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: nowrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ControlButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  min-width: auto;
  height: 32px;
  background: #f0f0f0;
  color: #1a1a1a;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #d4d4d4;
    border-color: #9a9a9a;
  }

  &:active:not(:disabled) {
    background: #b8b8b8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
  }
`;

const PlaybackRateSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  background: #f0f0f0;
  color: #1a1a1a;
  font-size: 12px;
  min-width: 60px;
  height: 32px;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #d4d4d4;
    border-color: #9a9a9a;
  }

  &:focus {
    outline: none;
    border-color: #5a5a5a;
  }
`;

const AddNoteButton = styled(ControlButton)`
  background: #1a1a1a;
  color: #ffffff;
  border-color: #1a1a1a;
  font-weight: 500;
  font-size: 12px;
  padding: 6px 10px;
  gap: 4px;

  &:hover:not(:disabled) {
    background: #000000;
    border-color: #000000;
  }

  &:active:not(:disabled) {
    background: #000000;
  }

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
  }
`;

const ShortcutsInfo = styled.div`
  margin-top: 12px;
  padding: 10px;
  background: #f0f0f0;
  border: 1px solid #c4c4c4;
  border-radius: 6px;
  font-size: 10px;
  color: #5a5a5a;
`;

const ShortcutsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 6px;
`;

const ShortcutItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Kbd = styled.span`
  background: #e8e8e8;
  border: 1px solid #c4c4c4;
  border-radius: 3px;
  padding: 2px 5px;
  font-size: 10px;
  font-family: monospace;
  color: #1a1a1a;
  font-weight: 600;
`;

function VideoPlayer({
  videoUrl,
  videoRef: externalVideoRef,
  currentTime,
  duration,
  playing,
  playbackRate,
  annotations = [],
  aiAnalysisMarkers = [],
  onTimeUpdate,
  onPlay,
  onPause,
  onSeek,
  onPlaybackRateChange,
  onTogglePlay,
  onSeekBackward,
  onSeekForward,
  onStartAnnotation,
  onDurationChange,
  onPlayerReady,
  onVideoError,
  playerReady = false,
}) {
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const { canCreateAnnotation } = useTrialPermissions();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoUrl, videoRef]);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      onTimeUpdate(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      onDurationChange(videoRef.current.duration);
      onPlayerReady();
    }
  };

  const handlePlay = () => {
    onPlay();
  };

  const handlePause = () => {
    onPause();
  };

  const handleSeek = e => {
    if (videoRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      onSeek(newTime);
    }
  };

  const handlePlaybackRateChange = e => {
    const rate = parseFloat(e.target.value);
    onPlaybackRateChange(rate);
  };

  const handleAnnotationClick = annotation => {
    onSeek(annotation.timestamp.start);
  };

  return (
    <div>
      <VideoContainer>
        <Video
          ref={videoRef}
          src={videoUrl}
          controls={false}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onError={onVideoError}
          onClick={onTogglePlay}
          preload="auto"
          playsInline
        />
        {playerReady && !playing && (
          <PlayOverlay onClick={onTogglePlay}>
            <PlayButton>
              <Play />
            </PlayButton>
          </PlayOverlay>
        )}
      </VideoContainer>

      <ControlsContainer>
        <ProgressContainer>
          <ProgressBar onClick={handleSeek}>
            <ProgressFill progress={duration ? (currentTime / duration) * 100 : 0} />
            {aiAnalysisMarkers.map(marker => {
              const position = duration ? (marker.seconds / duration) * 100 : 0;
              const clamped = Math.max(2, Math.min(98, position));
              const color = AI_ANALYSIS_SEVERITY_COLORS[marker.severity] || '#4A90E2';
              const severityLabel = marker.severity || 'Issue';
              const themeLabel = marker.theme || '';
              const markerTitle = themeLabel
                ? `${severityLabel}: ${themeLabel}`
                : severityLabel;

              return (
                <AIAnalysisMarker
                  key={marker.id}
                  $color={color}
                  style={{ left: `${clamped}%` }}
                  title={markerTitle}
                  onClick={e => {
                    e.stopPropagation();
                    onSeek(marker.seconds);
                  }}
                />
              );
            })}
            {annotations.map(annotation => {
              const position = duration
                ? (annotation.timestamp.start / duration) * 100
                : 0;
              return (
                <AnnotationMarker
                  key={annotation.annotationId}
                  emotion={annotation.emotion}
                  style={{
                    left: `${Math.max(2, Math.min(98, position))}%`,
                  }}
                  title={`${annotation.trialPart}: ${annotation.content.substring(
                    0,
                    50,
                  )}...`}
                  onClick={e => {
                    e.stopPropagation();
                    handleAnnotationClick(annotation);
                  }}
                />
              );
            })}
          </ProgressBar>
          <TimeDisplay>
            <span>{formatTime(Math.floor(currentTime))}</span>
            <span>{formatTime(Math.floor(duration))}</span>
          </TimeDisplay>
        </ProgressContainer>

        <ControlsRow>
          <ControlGroup>
            <ControlButton
              onClick={onSeekBackward}
              disabled={!playerReady}
              title="Seek back 10 seconds"
            >
              <SkipBack />
            </ControlButton>
            <ControlButton
              onClick={onTogglePlay}
              disabled={!playerReady}
              title="Play/Pause"
            >
              {playing ? <Pause /> : <Play />}
            </ControlButton>
            <ControlButton
              onClick={onSeekForward}
              disabled={!playerReady}
              title="Seek forward 10 seconds"
            >
              <SkipForward />
            </ControlButton>
            <PlaybackRateSelect value={playbackRate} onChange={handlePlaybackRateChange}>
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </PlaybackRateSelect>
          </ControlGroup>

          {onStartAnnotation && (
            <ControlGroup>
              <AddNoteButton
                onClick={onStartAnnotation}
                disabled={!playerReady || !canCreateAnnotation}
                title={
                  !canCreateAnnotation
                    ? 'You do not have permission to create annotations'
                    : 'Add annotation (A)'
                }
              >
                <Plus />
                Add Note
              </AddNoteButton>
            </ControlGroup>
          )}
        </ControlsRow>

        <ShortcutsInfo>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            Keyboard Shortcuts:
          </div>
          <ShortcutsGrid>
            <ShortcutItem>
              <Kbd>Space</Kbd>
              <span>Play/Pause</span>
            </ShortcutItem>
            {onStartAnnotation && (
              <ShortcutItem>
                <Kbd>A</Kbd>
                <span>Add Note</span>
              </ShortcutItem>
            )}
            <ShortcutItem>
              <Kbd>←</Kbd>
              <Kbd>→</Kbd>
              <span>Seek ±5s</span>
            </ShortcutItem>
            <ShortcutItem>
              <Kbd>Shift</Kbd>
              <Kbd>←</Kbd>
              <Kbd>→</Kbd>
              <span>Seek ±1s</span>
            </ShortcutItem>
            <ShortcutItem>
              <Kbd>Esc</Kbd>
              <span>Cancel</span>
            </ShortcutItem>
          </ShortcutsGrid>
        </ShortcutsInfo>
      </ControlsContainer>
    </div>
  );
}

VideoPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  videoRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  currentTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  playing: PropTypes.bool.isRequired,
  playbackRate: PropTypes.number.isRequired,
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      annotationId: PropTypes.string.isRequired,
      emotion: PropTypes.string,
      timestamp: PropTypes.shape({
        start: PropTypes.number.isRequired,
        end: PropTypes.number,
      }).isRequired,
      trialPart: PropTypes.string,
      content: PropTypes.string,
    }),
  ),
  aiAnalysisMarkers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      seconds: PropTypes.number.isRequired,
      severity: PropTypes.string,
      theme: PropTypes.string,
    }),
  ),
  onTimeUpdate: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onSeek: PropTypes.func.isRequired,
  onPlaybackRateChange: PropTypes.func.isRequired,
  onTogglePlay: PropTypes.func.isRequired,
  onSeekBackward: PropTypes.func.isRequired,
  onSeekForward: PropTypes.func.isRequired,
  onStartAnnotation: PropTypes.func,
  onDurationChange: PropTypes.func.isRequired,
  onPlayerReady: PropTypes.func.isRequired,
  onVideoError: PropTypes.func,
  playerReady: PropTypes.bool,
};

VideoPlayer.defaultProps = {
  videoRef: null,
  annotations: [],
  aiAnalysisMarkers: [],
  playerReady: false,
};

export default VideoPlayer;
